import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { saveAs } from 'file-saver';
import { useDropzone } from 'react-dropzone';
import { useCookies } from 'react-cookie';
import Swal from 'sweetalert2';
import {
  fetchSharepointGallery,
  buscarCarpetaDeStro,
  fetchCarpetaFijaDocs,
  CARPETAS_FIJAS,
  nroStroCorto,
  uploadSharepointFiles,
  renameSharepointFolder,
  validarNombreCarpeta,
  isValidSharepointUrl,
  formatFileSize,
  UPLOAD_MAX_FILE_SIZE,
  UPLOAD_MAX_FILES,
} from '../../AiChatWidget/sharepointService';
import './nuevoStroLight.css';

// ---------- helpers ----------
function base64ToBlob(base64, mimeType) {
  const byteChars = atob(base64);
  const byteNumbers = new Array(byteChars.length);
  for (let i = 0; i < byteChars.length; i++) byteNumbers[i] = byteChars.charCodeAt(i);
  return new Blob([new Uint8Array(byteNumbers)], { type: mimeType || 'application/octet-stream' });
}

function getExt(nombre = '') {
  const idx = nombre.lastIndexOf('.');
  return idx >= 0 ? nombre.slice(idx + 1).toLowerCase() : '';
}

function getKind(archivo) {
  const mime = archivo.mimeType || '';
  const ext = getExt(archivo.nombre);
  if (mime.startsWith('image/') || ['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp', 'svg'].includes(ext)) return 'image';
  if (mime === 'application/pdf' || ext === 'pdf') return 'pdf';
  if (mime.startsWith('video/') || ['mp4', 'mov', 'webm'].includes(ext)) return 'video';
  if (mime.startsWith('audio/') || ['mp3', 'ogg', 'wav'].includes(ext)) return 'audio';
  if (mime.includes('word') || ['doc', 'docx'].includes(ext)) return 'word';
  if (mime.includes('sheet') || mime.includes('excel') || ['xls', 'xlsx', 'csv'].includes(ext)) return 'excel';
  if (mime.includes('presentation') || ['ppt', 'pptx'].includes(ext)) return 'ppt';
  if (mime.includes('zip') || mime.includes('rar') || ['zip', 'rar', '7z'].includes(ext)) return 'zip';
  if (mime.startsWith('text/') || ['txt'].includes(ext)) return 'text';
  return 'file';
}

const KIND_ICON = {
  pdf: 'bi-file-earmark-pdf-fill',
  video: 'bi-file-earmark-play-fill',
  audio: 'bi-file-earmark-music-fill',
  word: 'bi-file-earmark-word-fill',
  excel: 'bi-file-earmark-excel-fill',
  ppt: 'bi-file-earmark-ppt-fill',
  zip: 'bi-file-earmark-zip-fill',
  text: 'bi-file-earmark-text-fill',
  image: 'bi-file-earmark-image-fill',
  file: 'bi-file-earmark-fill',
};

function formatFecha(archivo) {
  const raw = archivo.lastModified || archivo.fechaModificacion || archivo.modificado || archivo.ultimaModificacion || archivo.fecha;
  if (!raw) return null;
  const d = new Date(raw);
  if (isNaN(d.getTime())) return null;
  const dd = String(d.getDate()).padStart(2, '0');
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const yy = String(d.getFullYear()).slice(-2);
  return `${dd}/${mm}/${yy}`;
}

/**
 * Construye los blobs + object URLs de una lista de archivos del backend.
 * Va en un effect (y no en useMemo) para que el cleanup revoque exactamente las URLs
 * creadas, incluso con el doble mount de StrictMode.
 */
function useBlobDocs(archivos) {
  const [docs, setDocs] = useState([]);
  useEffect(() => {
    const construidos = archivos.map((a, i) => {
      const blob = base64ToBlob(a.base64 || '', a.mimeType);
      return {
        ...a,
        id: `${i}-${a.nombre}`,
        blob,
        url: URL.createObjectURL(blob),
        kind: getKind(a),
        ext: getExt(a.nombre),
        fecha: formatFecha(a),
      };
    });
    setDocs(construidos);
    return () => construidos.forEach(d => URL.revokeObjectURL(d.url));
  }, [archivos]);
  return docs;
}

// ---------- thumbnail ----------
function Miniatura({ doc }) {
  if (doc.kind === 'image') {
    return <img src={doc.url} alt={doc.nombre} className='documentosNubeThumbImg' loading='lazy' />;
  }
  if (doc.kind === 'pdf') {
    return (
      <div className='documentosNubeThumbPdf'>
        <iframe src={`${doc.url}#toolbar=0&navpanes=0&scrollbar=0&view=FitH`} title={doc.nombre} tabIndex={-1} loading='lazy' />
      </div>
    );
  }
  if (doc.kind === 'video') {
    return <video src={doc.url} className='documentosNubeThumbImg' muted preload='metadata' />;
  }
  return (
    <div className={`documentosNubeThumbIcon kind-${doc.kind}`}>
      <i className={`bi ${KIND_ICON[doc.kind] || KIND_ICON.file}`}></i>
      {doc.ext ? <span>{doc.ext.toUpperCase()}</span> : null}
    </div>
  );
}

// ---------- lightbox ----------
function VisorDocumento({ docs, index, onClose, onPrev, onNext }) {
  const doc = docs[index];

  useEffect(() => {
    const onKey = e => {
      if (e.key === 'Escape') onClose();
      else if (e.key === 'ArrowLeft') onPrev();
      else if (e.key === 'ArrowRight') onNext();
    };
    window.addEventListener('keydown', onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [onClose, onPrev, onNext]);

  if (!doc) return null;

  const descargar = () => saveAs(doc.blob, doc.nombre);
  const abrir = () => window.open(doc.url, '_blank', 'noopener,noreferrer');

  let contenido;
  if (doc.kind === 'image') {
    contenido = <img src={doc.url} alt={doc.nombre} className='documentosNubeVisorImg' />;
  } else if (doc.kind === 'pdf') {
    contenido = <iframe src={`${doc.url}#view=FitH`} title={doc.nombre} className='documentosNubeVisorFrame' />;
  } else if (doc.kind === 'video') {
    contenido = <video src={doc.url} controls autoPlay className='documentosNubeVisorImg' />;
  } else if (doc.kind === 'audio') {
    contenido = <audio src={doc.url} controls className='documentosNubeVisorAudio' />;
  } else {
    contenido = (
      <div className='documentosNubeVisorSinPreview'>
        <i className={`bi ${KIND_ICON[doc.kind] || KIND_ICON.file}`}></i>
        <p>Este tipo de archivo no tiene vista previa.</p>
        <button type='button' className='documentosNubeBtnPrimario' onClick={descargar}>
          <i className='bi bi-download me-2'></i>Descargar {doc.nombre}
        </button>
      </div>
    );
  }

  return createPortal(
    <div className='documentosNubeOverlay' onClick={onClose} role='dialog' aria-modal='true' aria-label={doc.nombre}>
      <div className='documentosNubeVisor' onClick={e => e.stopPropagation()}>
        <div className='documentosNubeVisorHeader'>
          <div className='documentosNubeVisorTitulo'>
            <i className={`bi ${KIND_ICON[doc.kind] || KIND_ICON.file} me-2`}></i>
            <span title={doc.nombre}>{doc.nombre}</span>
            <small>
              {formatFileSize(doc.size || 0)}
              {doc.fecha ? ` · ${doc.fecha}` : ''} · {index + 1}/{docs.length}
            </small>
          </div>
          <div className='documentosNubeVisorAcciones'>
            <button type='button' onClick={descargar} title='Descargar'>
              <i className='bi bi-download'></i>
            </button>
            <button type='button' onClick={abrir} title='Abrir en pestaña nueva'>
              <i className='bi bi-box-arrow-up-right'></i>
            </button>
            <button type='button' onClick={onClose} title='Cerrar (Esc)'>
              <i className='bi bi-x-lg'></i>
            </button>
          </div>
        </div>
        <div className='documentosNubeVisorBody'>{contenido}</div>
        {docs.length > 1 ? (
          <>
            <button type='button' className='documentosNubeVisorNav prev' onClick={onPrev} title='Anterior (←)'>
              <i className='bi bi-chevron-left'></i>
            </button>
            <button type='button' className='documentosNubeVisorNav next' onClick={onNext} title='Siguiente (→)'>
              <i className='bi bi-chevron-right'></i>
            </button>
          </>
        ) : null}
      </div>
    </div>,
    document.body
  );
}

/**
 * Estado de una pestaña de carpeta fija (A MORON, BAJAS SCAN): busca por nro de siniestro,
 * carga diferida (recién al abrir la pestaña) y se descarta al cambiar de siniestro.
 */
function useCarpetaFija(carpetaKey, nroStro, habilitada) {
  const [archivos, setArchivos] = useState([]);
  const [cargando, setCargando] = useState(false);
  const [error, setError] = useState(null);
  const [cargado, setCargado] = useState(false);
  const estadoRef = useRef({ cargando: false, cargado: false });
  estadoRef.current = { cargando, cargado };

  useEffect(() => {
    setArchivos([]);
    setError(null);
    setCargado(false);
  }, [nroStro, carpetaKey]);

  const cargar = useCallback(
    async (forzar = false) => {
      if (!habilitada) return;
      const { cargando: enCurso, cargado: yaEsta } = estadoRef.current;
      if (enCurso || (yaEsta && !forzar)) return;
      setCargando(true);
      setError(null);
      const res = await fetchCarpetaFijaDocs(carpetaKey, nroStro);
      if (res.success) {
        setArchivos(res.archivos);
      } else {
        setArchivos([]);
        setError(res.error || 'No se pudo consultar la carpeta.');
      }
      setCargado(true);
      setCargando(false);
    },
    [habilitada, carpetaKey, nroStro]
  );

  const docs = useBlobDocs(archivos);
  return { docs, cargando, error, cargado, cargar };
}

// ---------- main ----------
export default function DocumentosNubeLight({ stro }) {
  const [cookies] = useCookies(['user']);
  const puedeSubir = cookies?.user?.titulo !== 'lector';

  const link = stro?.link;
  const linkValido = !!link && isValidSharepointUrl(link);
  const linkHref = link ? (link.startsWith('http') ? link : `https://${link}`) : null;

  const [archivos, setArchivos] = useState([]);
  const [carpeta, setCarpeta] = useState('');
  const [cargando, setCargando] = useState(false);
  const [error, setError] = useState(null);
  const [busqueda, setBusqueda] = useState('');
  const [seleccionado, setSeleccionado] = useState(null); // índice dentro de docsFiltrados

  // Subida
  const [subiendo, setSubiendo] = useState(false);
  const [progreso, setProgreso] = useState(0);
  const [colaSubida, setColaSubida] = useState([]); // [{ nombre, size, estado: 'pendiente'|'ok'|'error', error? }]

  // Renombrar carpeta
  const [editandoNombre, setEditandoNombre] = useState(false);
  const [nombreBorrador, setNombreBorrador] = useState('');
  const [renombrando, setRenombrando] = useState(false);

  // Pestañas de carpetas fijas (solo contractuales): documentos cuyo nombre contiene
  // el nro de siniestro corto, dentro de carpetas compartidas del sitio.
  const esContractuales = cookies?.user?.depto === 'contractuales';
  const nroCorto = nroStroCorto(stro?.nroStro);
  const hayFijas = esContractuales && !!nroCorto;

  const [pestana, setPestana] = useState('carpeta'); // 'carpeta' | 'aMoron' | 'bajasScan'
  const esFija = pestana !== 'carpeta';

  const aMoron = useCarpetaFija('aMoron', stro?.nroStro, hayFijas);
  const bajasScan = useCarpetaFija('bajasScan', stro?.nroStro, hayFijas);
  const porKey = { aMoron, bajasScan };
  const fija = esFija ? porKey[pestana] : null;
  const carpetaFijaLabel = CARPETAS_FIJAS.find(c => c.key === pestana)?.label || '';

  // Sin link cargado: se busca la carpeta por nro de denuncia (así se crea) o de siniestro
  // en todas las bibliotecas del sitio, y al encontrarla el backend genera y guarda el link,
  // para no repetir la búsqueda en cada visita.
  const [candidatas, setCandidatas] = useState([]);
  const [carpetaHallada, setCarpetaHallada] = useState(null);
  const [linkVinculado, setLinkVinculado] = useState(null);
  const puedeBuscar = !linkValido && !!(stro?.nroStro || stro?.entryId);

  // Link que vale para operar (subir, renombrar, abrir): el de la denuncia o el recién vinculado
  const linkEfectivo = linkValido ? link : linkVinculado;
  const hayLink = !!linkEfectivo;

  const identidad = useMemo(() => ({ nroStro: stro?.nroStro, entryId: stro?.entryId, id: stro?.id }), [stro?.nroStro, stro?.entryId, stro?.id]);

  const cargarDesdeLink = useCallback(async url => {
    setCargando(true);
    setError(null);
    const res = await fetchSharepointGallery(url);
    if (res.success) {
      setArchivos(res.archivos);
      setCarpeta(res.carpetaNombre || '');
    } else {
      setArchivos([]);
      setError(res.error || 'No se pudieron cargar los documentos.');
    }
    setCargando(false);
  }, []);

  const cargar = useCallback(async () => {
    if (hayLink) {
      setCandidatas([]);
      await cargarDesdeLink(linkEfectivo);
      return;
    }

    if (!puedeBuscar) return;

    setCargando(true);
    setError(null);
    const res = await buscarCarpetaDeStro(identidad);
    if (res.success) {
      setCandidatas(res.carpetas);
      setCarpetaHallada(res.elegida);
      setArchivos(res.elegida ? res.archivos : []);
      setCarpeta(res.elegida?.nombre || '');
      if (res.link) setLinkVinculado(res.link);
    } else {
      setCandidatas([]);
      setCarpetaHallada(null);
      setArchivos([]);
      setError(res.error || 'No se pudo buscar la carpeta.');
    }
    setCargando(false);
  }, [hayLink, linkEfectivo, cargarDesdeLink, puedeBuscar, identidad]);

  /** Abre (y vincula) una de las candidatas cuando la búsqueda devolvió más de una. */
  const elegirCandidata = useCallback(
    async candidata => {
      setCargando(true);
      setError(null);
      const res = await buscarCarpetaDeStro(identidad, { carpetaId: candidata.id, driveId: candidata.driveId });
      if (res.success) {
        setCarpetaHallada(res.elegida || candidata);
        setArchivos(res.archivos);
        setCarpeta((res.elegida || candidata).nombre || '');
        if (res.link) setLinkVinculado(res.link);
      } else {
        setError(res.error || 'No se pudieron abrir los documentos de esa carpeta.');
      }
      setCargando(false);
    },
    [identidad]
  );

  useEffect(() => {
    setArchivos([]);
    setError(null);
    setBusqueda('');
    setSeleccionado(null);
    cargar();
  }, [cargar]);

  // Al cambiar de siniestro se vuelve a la pestaña de la carpeta propia
  useEffect(() => {
    setPestana('carpeta');
  }, [stro?.nroStro]);

  // Carga diferida: recién al abrir la pestaña
  useEffect(() => {
    if (fija) fija.cargar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pestana]);

  const docs = useBlobDocs(archivos);

  const docsFiltrados = useMemo(() => {
    if (fija) return fija.docs;
    const q = busqueda.trim().toLowerCase();
    if (!q) return docs;
    return docs.filter(d => d.nombre?.toLowerCase().includes(q));
  }, [docs, fija, busqueda]);

  // ---- subida de archivos (drag & drop / botón) ----
  const subirArchivos = useCallback(
    async files => {
      if (!puedeSubir || !hayLink || subiendo) return;
      let lista = Array.from(files || []);
      if (!lista.length) return;

      if (lista.length > UPLOAD_MAX_FILES) {
        await Swal.fire({ icon: 'warning', title: 'Demasiados archivos', text: `Podés subir hasta ${UPLOAD_MAX_FILES} archivos por vez.` });
        lista = lista.slice(0, UPLOAD_MAX_FILES);
      }

      const grandes = lista.filter(f => f.size > UPLOAD_MAX_FILE_SIZE);
      if (grandes.length) {
        await Swal.fire({
          icon: 'warning',
          title: 'Archivos demasiado grandes',
          html: `Se omiten (máx. ${formatFileSize(UPLOAD_MAX_FILE_SIZE)}):<br/><small>${grandes.map(f => f.name).join('<br/>')}</small>`,
        });
        lista = lista.filter(f => f.size <= UPLOAD_MAX_FILE_SIZE);
        if (!lista.length) return;
      }

      const existentes = new Set(docs.map(d => d.nombre?.toLowerCase()));
      const duplicados = lista.filter(f => existentes.has(f.name.toLowerCase()));
      if (duplicados.length) {
        const r = await Swal.fire({
          icon: 'question',
          title: 'Archivos ya existentes',
          html: `Ya existen en la carpeta:<br/><small>${duplicados.map(f => f.name).join('<br/>')}</small><br/><br/>¿Querés reemplazarlos?`,
          showCancelButton: true,
          confirmButtonText: 'Reemplazar',
          cancelButtonText: 'Omitir',
        });
        if (!r.isConfirmed) {
          lista = lista.filter(f => !existentes.has(f.name.toLowerCase()));
          if (!lista.length) return;
        }
      }

      setSubiendo(true);
      setProgreso(0);
      setColaSubida(lista.map(f => ({ nombre: f.name, size: f.size, estado: 'pendiente' })));

      const res = await uploadSharepointFiles(linkEfectivo, lista, setProgreso);

      const okNombres = new Set((res.subidos || []).map(s => s.nombre));
      const errPorNombre = Object.fromEntries((res.errores || []).map(e => [e.nombre, e.error]));
      setColaSubida(prev =>
        prev.map(item =>
          okNombres.has(item.nombre)
            ? { ...item, estado: 'ok' }
            : { ...item, estado: 'error', error: errPorNombre[item.nombre] || res.error || 'No se pudo subir.' }
        )
      );
      setSubiendo(false);

      if (res.success && !(res.errores || []).length) {
        // Todo OK: limpiar la cola tras un momento y refrescar la galería
        setTimeout(() => setColaSubida([]), 2500);
      } else if (!res.success && !(res.subidos || []).length) {
        Swal.fire({ icon: 'error', title: 'No se pudo subir', text: res.error || 'Error al subir a SharePoint.' });
      }
      if ((res.subidos || []).length) cargar();
    },
    [puedeSubir, hayLink, subiendo, docs, linkEfectivo, cargar]
  );

  const empezarEdicion = () => {
    setNombreBorrador(carpeta || '');
    setEditandoNombre(true);
  };

  const guardarNombre = async () => {
    const nuevo = nombreBorrador.trim();
    if (!nuevo || nuevo === carpeta) {
      setEditandoNombre(false);
      return;
    }
    const errorNombre = validarNombreCarpeta(nuevo);
    if (errorNombre) {
      Swal.fire({ icon: 'warning', title: 'Nombre inválido', text: errorNombre });
      return;
    }
    setRenombrando(true);
    const res = await renameSharepointFolder(linkEfectivo, nuevo);
    setRenombrando(false);
    if (res.success) {
      setCarpeta(res.carpetaNombre);
      setEditandoNombre(false);
    } else {
      Swal.fire({ icon: 'error', title: 'No se pudo renombrar', text: res.error });
    }
  };

  const { getRootProps, getInputProps, isDragActive, open: abrirSelector } = useDropzone({
    onDrop: subirArchivos,
    noClick: true, // los clics siguen abriendo miniaturas; para elegir archivos está el botón "Subir"
    noKeyboard: true,
    disabled: !puedeSubir || !hayLink || subiendo || esFija, // en las carpetas fijas no se sube nada
    multiple: true,
  });

  const cambiarPestana = destino => {
    setPestana(destino);
    setSeleccionado(null);
  };

  const cerrar = useCallback(() => setSeleccionado(null), []);
  const anterior = useCallback(() => setSeleccionado(i => (i === null ? null : (i - 1 + docsFiltrados.length) % docsFiltrados.length)), [docsFiltrados.length]);
  const siguiente = useCallback(() => setSeleccionado(i => (i === null ? null : (i + 1) % docsFiltrados.length)), [docsFiltrados.length]);

  return (
    <section {...getRootProps({ className: `documentosNubeCard ${isDragActive ? 'arrastrando' : ''}` })}>
      <input {...getInputProps()} />
      {isDragActive ? (
        <div className='documentosNubeDropOverlay'>
          <i className='bi bi-cloud-arrow-up-fill'></i>
          <span>Soltá para subir a la carpeta del siniestro</span>
        </div>
      ) : null}
      <div className='documentosNubeHeader'>
        <div className='documentosNubeTitulo'>
          <i className='bi bi-cloud-fill'></i>
          <div className='documentosNubeTituloTexto'>
            <span className='documentosNubeEyebrow'>Documentos nube</span>
            {editandoNombre ? (
              <div className='documentosNubeEditarNombre'>
                <input
                  type='text'
                  value={nombreBorrador}
                  autoFocus
                  disabled={renombrando}
                  onChange={e => setNombreBorrador(e.target.value)}
                  onKeyDown={e => {
                    if (e.key === 'Enter') guardarNombre();
                    else if (e.key === 'Escape') setEditandoNombre(false);
                  }}
                />
                <button type='button' onClick={guardarNombre} disabled={renombrando} title='Guardar (Enter)'>
                  {renombrando ? <span className='documentosNubeSpinner'></span> : <i className='bi bi-check-lg'></i>}
                </button>
                <button type='button' onClick={() => setEditandoNombre(false)} disabled={renombrando} title='Cancelar (Esc)'>
                  <i className='bi bi-x-lg'></i>
                </button>
              </div>
            ) : fija ? (
              <div className='documentosNubeTituloFila'>
                <h2>{carpetaFijaLabel}</h2>
                {fija.docs.length ? <span className='documentosNubeBadge'>{fija.docs.length}</span> : null}
              </div>
            ) : (
              <div className='documentosNubeTituloFila'>
                <h2 title={carpeta || ''}>{carpeta || (cargando ? 'Cargando carpeta…' : 'Carpeta del siniestro')}</h2>
                {puedeSubir && hayLink && carpeta ? (
                  <button type='button' className='documentosNubeEditarBtn' onClick={empezarEdicion} title='Editar nombre de la carpeta'>
                    <i className='bi bi-pencil'></i>
                  </button>
                ) : null}
                {docs.length ? <span className='documentosNubeBadge'>{docs.length}</span> : null}
              </div>
            )}
          </div>
        </div>
        <div className='documentosNubeAcciones'>
          {fija ? (
            <button type='button' className='documentosNubeIconBtn' onClick={() => fija.cargar(true)} disabled={fija.cargando} title={`Volver a buscar en ${carpetaFijaLabel}`}>
              <i className={`bi bi-arrow-clockwise ${fija.cargando ? 'documentosNubeGirando' : ''}`}></i>
            </button>
          ) : (
            <>
              <div className='documentosNubeBuscador'>
                <i className='bi bi-search'></i>
                <input type='text' placeholder='Buscar archivo...' value={busqueda} onChange={e => setBusqueda(e.target.value)} disabled={!docs.length} />
              </div>
              {puedeSubir && hayLink ? (
                <button type='button' className='documentosNubeBtnSubir' onClick={abrirSelector} disabled={subiendo} title='Subir archivos a la carpeta (o arrastralos acá)'>
                  {subiendo ? <span className='documentosNubeSpinner'></span> : <i className='bi bi-cloud-arrow-up'></i>}
                  Subir
                </button>
              ) : null}
              <button type='button' className='documentosNubeIconBtn' onClick={cargar} disabled={(!hayLink && !puedeBuscar) || cargando} title='Actualizar'>
                <i className={`bi bi-arrow-clockwise ${cargando ? 'documentosNubeGirando' : ''}`}></i>
              </button>
              {linkHref || carpetaHallada?.webUrl ? (
                <a className='documentosNubeIconBtn' href={linkHref || carpetaHallada.webUrl} target='_blank' rel='noopener noreferrer' title='Abrir carpeta en SharePoint'>
                  <i className='bi bi-box-arrow-up-right'></i>
                </a>
              ) : null}
            </>
          )}
        </div>
      </div>

      {hayFijas ? (
        <div className='documentosNubePestanas'>
          <button type='button' className={`documentosNubePestana ${!esFija ? 'documentosNubePestana--activa' : ''}`} onClick={() => cambiarPestana('carpeta')}>
            <i className='bi bi-folder2'></i>
            Carpeta del siniestro
          </button>
          {CARPETAS_FIJAS.map(c => {
            const estado = porKey[c.key];
            return (
              <button
                key={c.key}
                type='button'
                className={`documentosNubePestana ${pestana === c.key ? 'documentosNubePestana--activa' : ''}`}
                onClick={() => cambiarPestana(c.key)}>
                <i className='bi bi-file-earmark-pdf'></i>
                {c.label}
                {estado.cargado && estado.docs.length ? <span className='documentosNubePestanaBadge'>{estado.docs.length}</span> : null}
              </button>
            );
          })}
        </div>
      ) : null}

      {carpetaHallada && !esFija ? (
        <div className='documentosNubeAviso'>
          <i className={`bi ${linkVinculado ? 'bi-link-45deg' : 'bi-info-circle'}`}></i>
          <span>
            Carpeta encontrada por número en <strong>{carpetaHallada.biblioteca || 'otra biblioteca del sitio'}</strong>
            {linkVinculado ? ' y vinculada a la denuncia: la próxima vez abre directo.' : '. La denuncia no tiene link cargado.'}
          </span>
        </div>
      ) : null}

      {colaSubida.length && !esFija ? (
        <div className='documentosNubeCola'>
          <div className='documentosNubeColaHeader'>
            <span>
              {subiendo
                ? `Subiendo ${colaSubida.length} archivo${colaSubida.length > 1 ? 's' : ''}… ${progreso}%`
                : `${colaSubida.filter(i => i.estado === 'ok').length}/${colaSubida.length} subido${colaSubida.length > 1 ? 's' : ''}`}
            </span>
            {!subiendo ? (
              <button type='button' onClick={() => setColaSubida([])} title='Cerrar'>
                <i className='bi bi-x-lg'></i>
              </button>
            ) : null}
          </div>
          {subiendo ? (
            <div className='documentosNubeBarra'>
              <div style={{ width: `${progreso}%` }} />
            </div>
          ) : null}
          <ul>
            {colaSubida.map(item => (
              <li key={item.nombre} className={`estado-${item.estado}`} title={item.error || ''}>
                <i className={`bi ${item.estado === 'ok' ? 'bi-check-circle-fill' : item.estado === 'error' ? 'bi-x-circle-fill' : 'bi-arrow-repeat documentosNubeGirando'}`}></i>
                <span className='nombre'>{item.nombre}</span>
                <span className='meta'>{item.estado === 'error' ? item.error : formatFileSize(item.size)}</span>
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      {fija ? (
        fija.cargando ? (
          <div className='documentosNubeGrid'>
            {Array.from({ length: 2 }).map((_, i) => (
              <div key={i} className='documentosNubeItem documentosNubeSkeleton'>
                <div className='documentosNubeThumb' />
                <div className='documentosNubeNombre' />
                <div className='documentosNubeMeta' />
              </div>
            ))}
          </div>
        ) : fija.error ? (
          <div className='documentosNubeEstado error'>
            <i className='bi bi-exclamation-triangle'></i>
            <p>{fija.error}</p>
            <button type='button' className='documentosNubeBtnPrimario' onClick={() => fija.cargar(true)}>
              Reintentar
            </button>
          </div>
        ) : !fija.docs.length ? (
          <div className='documentosNubeEstado'>
            <i className='bi bi-file-earmark-x'></i>
            <p>
              No hay documento en {carpetaFijaLabel} para el siniestro {nroCorto}.
            </p>
          </div>
        ) : (
          <div className='documentosNubeGrid'>
            {fija.docs.map((doc, i) => (
              <button key={doc.id} type='button' className='documentosNubeItem' onClick={() => setSeleccionado(i)} title={doc.nombre}>
                <div className='documentosNubeThumb'>
                  <Miniatura doc={doc} />
                </div>
                <div className='documentosNubeNombre'>{doc.nombre}</div>
                <div className='documentosNubeMeta'>
                  {formatFileSize(doc.size || 0)}
                  {doc.fecha ? ` · ${doc.fecha}` : ''}
                </div>
              </button>
            ))}
          </div>
        )
      ) : cargando && !docs.length ? (
        <div className='documentosNubeGrid'>
          {Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className='documentosNubeItem documentosNubeSkeleton'>
              <div className='documentosNubeThumb' />
              <div className='documentosNubeNombre' />
              <div className='documentosNubeMeta' />
            </div>
          ))}
        </div>
      ) : error ? (
        <div className='documentosNubeEstado error'>
          <i className='bi bi-exclamation-triangle'></i>
          <p>{error}</p>
          <button type='button' className='documentosNubeBtnPrimario' onClick={cargar}>
            Reintentar
          </button>
        </div>
      ) : !hayLink && !carpetaHallada ? (
        candidatas.length ? (
          <div className='documentosNubeCandidatas'>
            <p>
              <i className='bi bi-folder2-open me-2'></i>
              Se encontraron {candidatas.length} carpetas que coinciden con este siniestro. Elegí cuál corresponde:
            </p>
            {candidatas.map(c => (
              <button key={`${c.driveId}-${c.id}`} type='button' className='documentosNubeCandidata' onClick={() => elegirCandidata(c)}>
                <i className='bi bi-folder-fill'></i>
                <span className='documentosNubeCandidataNombre'>
                  {c.nombre}
                  <small>{c.biblioteca}</small>
                </span>
                {typeof c.cantidadArchivos === 'number' ? <span className='documentosNubeBadge'>{c.cantidadArchivos}</span> : null}
                <i className='bi bi-chevron-right'></i>
              </button>
            ))}
          </div>
        ) : (
          <div className='documentosNubeEstado'>
            <i className='bi bi-cloud-slash'></i>
            <p>
              Este siniestro no tiene un link de SharePoint vinculado
              {puedeBuscar ? ' y no se encontró ninguna carpeta con su número en las bibliotecas del sitio.' : '.'}
            </p>
            {puedeBuscar ? (
              <button type='button' className='documentosNubeBtnPrimario' onClick={cargar}>
                Buscar de nuevo
              </button>
            ) : null}
          </div>
        )
      ) :!docs.length ? (
        <div className='documentosNubeEstado'>
          <i className='bi bi-folder2-open'></i>
          <p>La carpeta no tiene documentos.{puedeSubir ? ' Arrastrá archivos acá o usá "Subir".' : ''}</p>
        </div>
      ) : !docsFiltrados.length ? (
        <div className='documentosNubeEstado'>
          <i className='bi bi-search'></i>
          <p>Ningún archivo coincide con "{busqueda}".</p>
        </div>
      ) : (
        <div className='documentosNubeGrid'>
          {docsFiltrados.map((doc, i) => (
            <button key={doc.id} type='button' className='documentosNubeItem' onClick={() => setSeleccionado(i)} title={doc.nombre}>
              <div className='documentosNubeThumb'>
                <Miniatura doc={doc} />
              </div>
              <div className='documentosNubeNombre'>{doc.nombre}</div>
              <div className='documentosNubeMeta'>
                {formatFileSize(doc.size || 0)}
                {doc.fecha ? ` · ${doc.fecha}` : ''}
              </div>
            </button>
          ))}
        </div>
      )}

      {seleccionado !== null && docsFiltrados[seleccionado] ? (
        <VisorDocumento docs={docsFiltrados} index={seleccionado} onClose={cerrar} onPrev={anterior} onNext={siguiente} />
      ) : null}
    </section>
  );
}
